import { NavLink } from "react-router-dom";
import {
  ArrowLeftRight,
  LayoutDashboard,
  type LucideIcon,
  Settings,
  Wallet,
} from "lucide-react";

import { cn } from "@/lib/cn";

/**
 * Fixed-bottom 4-tab bar shown only on viewports under the desktop breakpoint
 * (AppShell decides the gating). Each tab is a NavLink so React Router owns
 * the active state. Hit targets are ≥ 44×44px per spec §9.0.
 *
 * `padding-bottom: env(safe-area-inset-bottom)` keeps the labels above the
 * iOS home-indicator on devices with a notch.
 */

interface MobileTab {
  to: string;
  label: string;
  icon: LucideIcon;
}

const TABS: MobileTab[] = [
  { to: "/dashboard", label: "Home", icon: LayoutDashboard },
  { to: "/wallets", label: "Wallets", icon: Wallet },
  // "Tx"/"Me" were cryptic; the 10px labels have room for the full words and
  // the Settings glyph matches the sidebar's Settings row.
  { to: "/transactions", label: "Activity", icon: ArrowLeftRight },
  { to: "/settings", label: "Settings", icon: Settings },
];

export function MobileTabBar() {
  return (
    <nav
      role="navigation"
      aria-label="Mobile tabs"
      className={cn(
        "fixed inset-x-0 bottom-0 z-30 grid grid-cols-4",
        "border-t border-[var(--line)] bg-[var(--bg)]",
      )}
      style={{
        height: "60px",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {TABS.map((tab) => (
        <MobileTabLink key={tab.to} tab={tab} />
      ))}
    </nav>
  );
}

function MobileTabLink({ tab }: { tab: MobileTab }) {
  const Icon = tab.icon;
  return (
    <NavLink
      to={tab.to}
      className={({ isActive }) =>
        cn(
          "flex min-h-[44px] flex-col items-center justify-center gap-1",
          "text-[11px] font-medium",
          "transition-colors duration-fast ease",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--indigo)]",
          isActive
            ? "text-[var(--indigo)]"
            : "text-[var(--ink-3)] hover:text-[var(--ink)]",
        )
      }
    >
      <Icon className="h-[18px] w-[18px]" strokeWidth={1.7} aria-hidden />
      <span>{tab.label}</span>
    </NavLink>
  );
}
